import { useState } from 'react';
import { adminApi } from './api';
import { downloadXlsxTemplate } from './downloadXlsxTemplate';

const MAX_BYTES = 2 * 1024 * 1024;
export default function BulkQuestionImport({ kind, subjectId, onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheetChoice, setSheetChoice] = useState('');
  const [sheetSearch, setSheetSearch] = useState('');
  const [sheetDialog, setSheetDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function exportTemplate() {
    try {
      setError('');
      await downloadXlsxTemplate('questions');
    } catch (failure) {
      setError(failure.message || 'Téléchargement du modèle Excel impossible.');
    }
  }

  async function chooseFile(event) {
    const selected = event.target.files?.[0] || null;
    setFile(null);
    setPreview(null);
    setSuccess('');
    setError('');
    setSheetDialog(false);
    setSelectedSheet('');
    setSheetChoice('');
    setSheetSearch('');
    setSheets([]);
    if (!selected) return;
    const name = selected.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.json')) {
      setError('Choisissez un fichier Excel .xlsx ou JSON .json.');
      return;
    }
    if (selected.size > MAX_BYTES) {
      setError('Fichier trop volumineux : maximum 2 Mo.');
      return;
    }
    setBusy(true);
    try {
      if (name.endsWith('.xlsx')) {
        const result = await adminApi.inspectWorkbookSheets(selected);
        if (!result.sheets?.length) throw new Error('Aucune feuille Excel trouvée.');
        setFile(selected);
        setSheets(result.sheets);
        setSheetChoice(result.sheets[0]);
        setSheetDialog(true);
      } else {
        const jsonText = typeof selected.text === 'function'
          ? await selected.text()
          : await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(new Error('Lecture JSON impossible'));
            reader.readAsText(selected, 'utf-8');
          });
        const payload = JSON.parse(jsonText);
        if (payload.version !== 1 || !Array.isArray(payload.questions)) {
          throw new Error('JSON attendu : version 1 et tableau questions.');
        }
        setFile(selected);
      }
    } catch (err) {
      setError(err.message || 'Impossible de lire ce fichier.');
    } finally {
      setBusy(false);
    }
  }

  async function run(dryRun) {
    if (!file || busy || (file.name.toLowerCase().endsWith('.xlsx') && !selectedSheet)) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const result = await adminApi.bulkImportQuestions(
        kind, subjectId, file, dryRun, dryRun ? '' : preview?.sha256, selectedSheet
      );
      if (dryRun) {
        setPreview(result);
      } else {
        setSuccess(result.imported + ' question(s) importée(s) en brouillon. ' +
          result.duplicate_count + ' doublon(s) ignoré(s).');
        setPreview(null);
        setFile(null);
        setSelectedSheet('');
        await onImported?.();
      }
    } catch (err) {
      setError(err.message || 'Importation impossible.');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bo-panel bo-bulk-import" aria-label="Importation massive des questions">
      <div className="bo-row bo-row--between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h4>Importer des questions depuis Excel ou JSON</h4>
          <p className="bo-muted">Même édition, corrections et publication qu'une question créée manuellement.</p>
        </div>
        <button type="button" className="bo-button bo-button--ghost"
          aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {open ? 'Fermer l’import' : 'Importer des questions (Excel / JSON)'}
        </button>
      </div>
      {open && (
        <div className="bo-stack">
          <p className="bo-muted">
            Excel : utilisez le modèle, enregistrez-le en .xlsx et choisissez sa feuille après la sélection.
            JSON : utilisez un objet version 1 avec un tableau questions et leurs propositions.
            Maximum 1 000 questions par import.
          </p>
          <div className="bo-actions">
            <button type="button" className="bo-button bo-button--ghost" onClick={exportTemplate}>
              Télécharger le modèle Excel (.xlsx)
            </button>
            <button type="button" className="bo-button bo-button--ghost"
              onClick={() => {
                const example = {
                  version: 1,
                  questions: [{
                    question_text: 'Question de démonstration',
                    type_question: 'qcm',
                    choices: [
                      { text: 'Réponse A', is_correct: true },
                      { text: 'Réponse B', is_correct: false },
                    ],
                    explication: 'Explication de démonstration',
                    difficulte: 'moyen',
                  }],
                };
                const blob = new Blob([JSON.stringify(example, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'modele_questions.json';
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
              }}>
              Télécharger le modèle JSON
            </button>
          </div>
          <label>
            Fichier Excel ou JSON à importer
            <input className="bo-input" type="file"
              accept=".xlsx,.json,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={chooseFile} disabled={busy}/>
          </label>
          {file && <p className="bo-muted">
            Fichier choisi : <strong>{file.name}</strong>
            {selectedSheet && <> · Feuille sélectionnée : <strong>{selectedSheet}</strong>
              {' '}<button type="button" className="bo-button bo-button--ghost"
                onClick={() => { setSheetDialog(true); setSheetChoice(selectedSheet); setPreview(null); }}>
                Changer de feuille
              </button></>}
          </p>}
          {sheetDialog && (
            <div role="dialog" aria-modal="true" aria-labelledby={'sheet-dialog-' + kind + subjectId}
              className="bo-panel bo-sheet-choice" style={{ border: '2px solid var(--bo-primary, #3569aa)', padding: 20 }}>
              <h4 id={'sheet-dialog-' + kind + subjectId}>Choisir la feuille Excel à importer</h4>
              <p className="bo-muted">Seule la feuille choisie sera vérifiée puis importée. Les autres feuilles ne seront pas modifiées.</p>
              <label>Filtrer les feuilles par nom
          <input className="bo-input" type="search" value={sheetSearch}
            placeholder="Rechercher un onglet…"
            onChange={(e) => setSheetSearch(e.target.value)}/>
        </label>
              <label>
                Feuille du classeur
                <select className="bo-input" value={sheetChoice}
                  onChange={(e) => setSheetChoice(e.target.value)}>
                  {sheets.filter(name => name.toLocaleLowerCase().includes(sheetSearch.toLocaleLowerCase())).map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
              <div className="bo-actions">
                <button type="button" className="bo-button bo-button--primary" disabled={!sheets.filter(name => name.toLocaleLowerCase().includes(sheetSearch.toLocaleLowerCase())).includes(sheetChoice)} onClick={() => {
                  setSelectedSheet(sheetChoice);
                  setPreview(null);
                  setSheetDialog(false);
                }}>Utiliser cette feuille</button>
                <button type="button" className="bo-button bo-button--ghost"
                  onClick={() => setSheetDialog(false)}>Annuler</button>
              </div>
            </div>
          )}
          <div className="bo-actions">
            <button type="button" className="bo-button bo-button--secondary"
              disabled={!file || busy || sheetDialog || (sheets.length > 0 && !selectedSheet)}
              onClick={() => run(true)}>
              {busy ? 'Analyse en cours…' : 'Vérifier sans importer'}
            </button>
          </div>
          {preview && !sheetDialog && (
            <div role="status" className="bo-panel">
              <strong>Aperçu vérifié : {preview.total} ligne(s) ; {preview.new_count} nouvelle(s) question(s) ;
                {' '}{preview.duplicate_count} doublon(s) ignoré(s).</strong>
              {selectedSheet && <p>Feuille : <strong>{selectedSheet}</strong></p>}
              <p className="bo-muted">Destination : sujet en brouillon. Aucun candidat ne voit ces questions avant publication.</p>
              <ul>
                {(preview.sample || []).map((question, index) => (
                  <li key={index}>{question.question_text} ({question.choices_count} choix)</li>
                ))}
              </ul>
              <button type="button" className="bo-button bo-button--primary"
                disabled={busy || preview.new_count === 0}
                onClick={() => run(false)}>Confirmer l’import en brouillon</button>
            </div>
          )}
          {error && <p role="alert" className="bo-error">{error}</p>}
          {success && <p role="status">{success}</p>}
        </div>
      )}
    </section>
  );
}
